import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import type { PeerTenant } from '@/types';

interface PartyDetailDialogProps {
  party: PeerTenant | null;
  onClose: () => void;
  onViewOutstanding?: (party: PeerTenant) => void;
}

const TENANT_TYPE_PALETTE: Record<string, string> = {
  RETAILER:    '#C17BAF',
  WHOLESALER:  '#7B61FF',
  DISTRIBUTOR: '#3DD68C',
  OTHERS:      '#9CA3AF',
};

export function PartyDetailDialog({ party, onClose, onViewOutstanding }: PartyDetailDialogProps) {
  if (!party) return null;
  const outstanding = party.linkedTenantParty?.outstanding;
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth onClick={e => e.stopPropagation()}>
      <DialogTitle sx={{ fontSize: 15, fontWeight: 700, pb: 1, pr: 10, display: 'flex', alignItems: 'center', gap: 1 }}>
        {party.name}
        {party.tenantType && (
          <Box component="span" sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5, whiteSpace: 'nowrap', flexShrink: 0, color: TENANT_TYPE_PALETTE[party.tenantType] ?? TENANT_TYPE_PALETTE.OTHERS }}>
            {party.tenantType}
          </Box>
        )}
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          ✕
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        {outstanding != null && (
          <Box
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 0.6, mb: 0.5,
              borderBottom: '1px solid #f3f4f6',
              cursor: onViewOutstanding ? 'pointer' : 'default',
              '&:hover': onViewOutstanding ? { bgcolor: '#fef2f2' } : {},
            }}
            onClick={() => onViewOutstanding?.(party)}
          >
            <Typography sx={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>Outstanding</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: outstanding > 0 ? '#dc2626' : '#16a34a', textDecoration: onViewOutstanding ? 'underline' : 'none' }}>
              ₹{outstanding.toLocaleString()}
            </Typography>
          </Box>
        )}
        {[
          { label: 'Type', value: party.tenantType },
          { label: 'Industry', value: party.industry },
          { label: 'Phone', value: party.phone },
          { label: 'Email', value: party.email },
          { label: 'Area', value: party.area },
          { label: 'City', value: party.city },
          { label: 'District', value: party.district },
          { label: 'State', value: party.state },
          { label: 'Pincode', value: party.pincode },
          { label: 'Address', value: party.address },
          { label: 'GST No', value: party.gstin },
          { label: 'Drug License', value: party.drugLicenseNumber },
          { label: 'Food License', value: party.foodLicenseNumber },
        ].filter(r => r.value).map(r => (
          <Box key={r.label} sx={{ display: 'flex', gap: 2, py: 0.6, borderBottom: '1px solid #f3f4f6' }}>
            <Typography sx={{ fontSize: 12, color: '#6b7280', width: 110, flexShrink: 0, fontWeight: 600 }}>{r.label}</Typography>
            <Typography sx={{ fontSize: 13, color: '#111827', wordBreak: 'break-all' }}>{r.value}</Typography>
          </Box>
        ))}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button size="small" onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
